/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { PageLayout } from '@kcftech/design-system';
import { ICorporationResponse, ILocationResponse } from '@kcftech/prometheus-client';
import { TabRouter } from 'components/tabs/TabRouter';
import { useFeatureFlag } from 'configcat-react';
import { PermissionsContext, usePermissions } from 'contexts/PermissionsContext';
import { useRouteTabs } from 'custom_hooks/data_hooks/data_source/useRouteTabs';
import { ContentTabConfig } from 'custom_hooks/data_hooks/data_source/useTabs';
import { AnalyticsPage } from 'pages/Models/AnalyticsModelTab/AnalyticsPage';
import { AssemblyTypesPage } from 'pages/Settings/AssemblyType/AssemblyTypesPage';
import { AssetTypesPage } from 'pages/Settings/AssetType/AssetTypesPage';
import { BearingsPage } from 'pages/Settings/Bearings/BearingsPage';
import { CorporationsPage } from 'pages/Settings/Corporations/CorporationsPage';
import { SingleCorporationPage } from 'pages/Settings/Corporations/SingleCorporationPage';
import { FileLibraryPage } from 'pages/Settings/FileLibrary/FileLibraryPage';
import { IdentityProviders } from 'pages/Settings/IdentityProvidersPage/IdentityProviders';
import { IndustriesPage } from 'pages/Settings/Industries/IndustriesPage';
import { IntegrationsPage } from 'pages/Settings/Integrations/IntegrationsPage';
import { LocationsPage } from 'pages/Settings/Locations/LocationsPage';
import { SingleLocationPage } from 'pages/Settings/Locations/SingleLocationPage';
import { NotificationSubscriptionsRouter } from 'pages/Settings/NotificationSubscriptions/NotificationSubscriptionsRouter';
import { SetupToolsPage } from 'pages/Settings/SetupTools/SetupToolsPage';
import { TemplatesPage } from 'pages/Settings/Templates/TemplatesPage';
import { ThirdPartySensorsRouter } from 'pages/Settings/ThirdPartySensorModels/ThirdPartySensorsRouter';
import { TimeRanges } from 'pages/Settings/TimeRanges/TimeRanges';
import { UsersPage } from 'pages/Settings/Users/UsersPage';
import { V4SensorSettingsPage } from 'pages/Settings/V4SensorSettings/V4SensorSettingsPage';
import { useMemo } from 'react';

const RenderAtAll: RenderAt[] = ['Global', 'Corporation', 'Location'];
export type RenderAt = 'Global' | 'Corporation' | 'Location';

const hasFlag = (renderAtUnion: RenderAt[], flag: RenderAt) =>
    renderAtUnion.includes(flag);

export const shouldRenderSettingsRoute = (
    route: string,
    targetLevel: RenderAt,
): boolean => {
    const routeDefinition = SettingsRouteDefinitions[route as SettingsRoute];
    // This is introduced by the above cast
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (routeDefinition === undefined) {
        return false;
    }
    return hasFlag(routeDefinition.renderAt, targetLevel);
};

type SettingsTabConfig = Omit<ContentTabConfig, 'id' | 'content'> &
    Readonly<{
        id: SettingsRoute;
        renderAt: RenderAt[];
        contentFn: (ctx: SettingsPageContext) => JSX.Element;
        permissionsCheck: (perms: PermissionsContext) => boolean;
    }>;

export type SettingsRoute = (typeof SettingsRouteNames)[number];

const SettingsRouteNames = [
    'models',
    'applicationTypes',
    'assetTypes',
    'bearings',
    'corporation',
    'corporations',
    'fileLibrary',
    'identityProviders',
    'industries',
    'integrations',
    'location',
    'locations',
    'notificationSubscriptions',
    'setupTools',
    'templates',
    'thirdPartySensors',
    'timeRanges',
    'users',
    'v4SensorSettings',
] as const;

type SettingsPageContext = Readonly<{
    corporation: ICorporationResponse | undefined;
    location: ILocationResponse | undefined;
}>;

export const SettingsRouteDefinitions: Record<SettingsRoute, SettingsTabConfig> = {
    models: {
        id: 'models',
        label: 'Analytic Models',
        renderAt: RenderAtAll,
        permissionsCheck: (perms) => perms.canViewModel,
        contentFn: ({ corporation, location }) => (
            <AnalyticsPage corporation={corporation} location={location} />
        ),
    },
    applicationTypes: {
        id: 'applicationTypes',
        label: 'Application Types',
        renderAt: ['Global'],
        permissionsCheck: (perms) => perms.canViewAssemblyTypeSettings,
        contentFn: () => <AssemblyTypesPage />,
    },
    assetTypes: {
        id: 'assetTypes',
        label: 'Asset Types',
        renderAt: ['Global'],
        permissionsCheck: (perms) => perms.canViewAssetTypeSettings,
        contentFn: () => <AssetTypesPage />,
    },
    bearings: {
        id: 'bearings',
        label: 'Bearings',
        renderAt: RenderAtAll,
        permissionsCheck: (perms) => perms.canViewBearingsSettings,
        contentFn: ({ corporation, location }) => (
            <BearingsPage corporation={corporation} location={location} />
        ),
    },
    corporation: {
        id: 'corporation',
        label: 'Corporation',
        renderAt: ['Corporation', 'Location'],
        permissionsCheck: (perms) => perms.canViewCorporationsSettings,
        contentFn: ({ corporation }) => (
            <SingleCorporationPage corporationId={corporation!.Id} />
        ),
    },
    corporations: {
        id: 'corporations',
        label: 'Corporations',
        renderAt: ['Global'],
        permissionsCheck: (perms) => perms.canViewCorporationsSettings,
        contentFn: ({ corporation }) => <CorporationsPage corporation={corporation} />,
    },
    fileLibrary: {
        id: 'fileLibrary',
        label: 'File Library',
        renderAt: ['Corporation', 'Location'],
        permissionsCheck: (perms) => perms.canViewFileLibrary,
        contentFn: ({ corporation, location }) => (
            <FileLibraryPage corporation={corporation} location={location} />
        ),
    },
    identityProviders: {
        id: 'identityProviders',
        label: 'Identity Providers',
        renderAt: ['Global'],
        permissionsCheck: (perms) => perms.canViewIdentityProviders,
        contentFn: () => <IdentityProviders />,
    },
    industries: {
        id: 'industries',
        label: 'Industries',
        renderAt: ['Global'],
        permissionsCheck: (perms) => perms.canViewIndustries,
        contentFn: () => <IndustriesPage />,
    },
    integrations: {
        id: 'integrations',
        label: 'Integrations',
        renderAt: ['Location'],
        permissionsCheck: (perms) => perms.canViewIntegrations,
        contentFn: ({ location }) =>
            location ? <IntegrationsPage location={location} /> : <></>,
    },
    location: {
        id: 'location',
        label: 'Location',
        renderAt: ['Location'],
        permissionsCheck: (perms) => perms.canViewLocationsSettings,
        contentFn: ({ location }) => <SingleLocationPage locationId={location!.Id} />,
    },
    locations: {
        id: 'locations',
        label: 'Locations',
        renderAt: ['Global', 'Corporation'],
        permissionsCheck: (perms) => perms.canViewLocationsSettings,
        contentFn: ({ corporation, location }) => (
            <LocationsPage corporation={corporation} location={location} />
        ),
    },
    notificationSubscriptions: {
        id: 'notificationSubscriptions',
        label: 'Notification Subscriptions',
        renderAt: RenderAtAll,
        permissionsCheck: (perms) => perms.canViewNotificationSubscriptionsSettings,
        contentFn: ({ corporation, location }) => (
            <NotificationSubscriptionsRouter
                corporation={corporation}
                location={location}
            />
        ),
    },
    setupTools: {
        id: 'setupTools',
        label: 'Setup Tools',
        renderAt: RenderAtAll,
        permissionsCheck: (perms) => perms.canViewSetupTools,
        contentFn: ({ corporation, location }) => (
            <SetupToolsPage corporation={corporation} location={location} />
        ),
    },
    templates: {
        id: 'templates',
        label: 'Templates',
        renderAt: ['Location'],
        permissionsCheck: (perms) => perms.canViewTemplates,
        contentFn: ({ location }) =>
            location ? <TemplatesPage location={location} /> : <></>,
    },
    thirdPartySensors: {
        id: 'thirdPartySensors',
        label: 'Third Party Sensor Models',
        renderAt: ['Global'],
        permissionsCheck: (perms) => perms.canViewThirdPartySensors,
        contentFn: () => <ThirdPartySensorsRouter />,
    },
    timeRanges: {
        id: 'timeRanges',
        label: 'Time Ranges',
        renderAt: ['Global'],
        permissionsCheck: (perms) => perms.canViewTimeRangesSettings,
        contentFn: () => <TimeRanges />,
    },
    users: {
        id: 'users',
        label: 'Users',
        renderAt: RenderAtAll,
        permissionsCheck: (perms) => perms.canViewUsers,
        contentFn: ({ corporation, location }) => (
            <UsersPage corporation={corporation} location={location} />
        ),
    },
    v4SensorSettings: {
        id: 'v4SensorSettings',
        label: 'V4 Sensor Settings',
        renderAt: ['Location'],
        permissionsCheck: (perms) => perms.canViewV4SensorSettings,
        contentFn: ({ location }) =>
            location ? <V4SensorSettingsPage location={location} /> : <></>,
    },
};

export const getTabs = (
    corporation: ICorporationResponse | undefined,
    location: ILocationResponse | undefined,
    permissions: PermissionsContext,
    v4SensorsEnabled: boolean,
): ContentTabConfig[] => {
    const ctx = { corporation, location };
    const level = (() => {
        if (!corporation) {
            return 'Global';
        }
        if (!location) {
            return 'Corporation';
        }
        return 'Location';
    })();
    return SettingsRouteNames.map((routeName) => SettingsRouteDefinitions[routeName])
        .filter((def) => hasFlag(def.renderAt, level))
        .filter((def) => def.permissionsCheck(permissions))
        .filter((def) => v4SensorsEnabled || def.id !== 'v4SensorSettings')
        .map((def) => ({
            ...def,
            content: def.contentFn(ctx),
        }));
};

export type SettingsPageProps = Readonly<{
    corporation?: ICorporationResponse;
    location?: ILocationResponse;
}>;

export function SettingsPage({ corporation, location }: SettingsPageProps) {
    const permissions = usePermissions();
    const { value: v4SensorsEnabled } = useFeatureFlag('v4Sensors', false);
    const { routeTabs } = useRouteTabs(
        useMemo(
            () => getTabs(corporation, location, permissions, v4SensorsEnabled),
            [corporation, location, permissions, v4SensorsEnabled],
        ),
    );

    return (
        <PageLayout layout="task">
            <TabRouter routeTabs={routeTabs} />
        </PageLayout>
    );
}
